import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { wallClockAt } from '@/features/document-overlay/recording-time'
import { followPlayback } from '@/features/document-overlay/video-clock'
import type { SubtitleTrack } from '@/features/document-overlay/video-subtitles'
import {
  boxStyle,
  type ContentRect,
  contentRect,
  objectsAt,
  type PassBar,
  type PassPlayRequest,
  type PlateBar,
  passBars,
  plateBars,
  platesAt,
  tracksAt,
} from '@/features/document-overlay/video-tracks'
import type { VideoObjectTrack, VideoPlateTrack, VideoTrack } from '@/services/api/video'
import { cn } from '@/utils/cn'
import { formatClockTime } from '@/utils/date'
import { formatVideoTime } from '@/utils/video-file'

const VIDEO = 'documents.detail.video'

/** The tone of a pass on the picture and on the strip: the face's state, or none (token classes only). */
const TRACK_TONE = {
  confirmed: { box: 'border-success', bar: 'bg-success/80', dot: 'bg-success' },
  suggested: { box: 'border-warning', bar: 'bg-warning/80', dot: 'bg-warning' },
  unassigned: {
    box: 'border-muted-foreground',
    bar: 'bg-muted-foreground/60',
    dot: 'bg-muted-foreground',
  },
  none: { box: 'border-foreground/50', bar: 'bg-foreground/30', dot: 'bg-foreground/50' },
} as const satisfies Record<PassBar['state'], { box: string; bar: string; dot: string }>

/** The tone of a plate on the picture and the strip: its link status (token classes only). */
const PLATE_TONE = {
  linked: { box: 'border-info', bar: 'bg-info/80' },
  suggested: { box: 'border-info/60 border-dashed', bar: 'bg-info/40' },
  rejected: { box: 'border-muted-foreground/60 border-dashed', bar: 'bg-muted-foreground/30' },
} as const satisfies Record<PlateBar['status'], { box: string; bar: string }>

/** A pass is drawn on this many lanes at most; later overlaps share the last lane. */
const MAX_LANES = 4
const LANE_HEIGHT = 10

export interface VideoWithTracksProps {
  /** The playback copy's URL (ticketed, fetched by the element itself); null while unknown. */
  src: string | null
  /** The upright picture's size, so the boxes have a frame before the metadata arrives. */
  width: number
  height: number
  duration: number
  tracks: readonly VideoTrack[]
  /** The plates' passes (slice 07): a box with the number as it plays, a bar on the strip. */
  plates?: readonly VideoPlateTrack[]
  /** The vehicles and other objects tracked beside the people: a box with the class as it plays. */
  objects?: readonly VideoObjectTrack[]
  /** The pass emphasised on the picture and the strip; null draws every pass the same. */
  selectedTrackId: number | null
  /** Picking a box or a bar; omit for a read-only player. */
  onSelectTrack?: (trackId: number | null) => void
  /** "Play pass": the player seeks to `from`, plays, and pauses at `to`, once per nonce. */
  playRequest?: PassPlayRequest | null
  /** When the recording starts (ISO 8601): the strip then reads the time of day at the playhead. */
  recordedAt?: string | null
  /** The playhead as it moves, for a link that opens the recording where it stands. */
  onTime?: (seconds: number) => void
  /** What was said, as a subtitle track under the player's captions control (PRD-0115); null for none. */
  subtitles?: SubtitleTrack | null
  className?: string
}

/** The label of a pass: the confirmed name, or the person's number in the recording. */
function useTrackLabel() {
  const { t } = useTranslation()
  return (trackId: number, name: string | null | undefined) =>
    name ?? t(`${VIDEO}.person`, { id: trackId })
}

/**
 * The recording with every tracked person boxed as it plays, and the passes laid on its
 * timeline underneath (PRD-0074 SC-016). The boxes follow the engine's trajectories,
 * interpolated between analysed frames, in the picture's own letterboxed rectangle; a box
 * carries the name the analyst confirmed or the person's number. The strip is the recording
 * at a glance: one bar per pass in its tone, the playhead over them, a click seeks to the pass.
 * Pure: the tracks, the selection and the play request all arrive as props.
 */
export function VideoWithTracks({
  src,
  width,
  height,
  duration,
  tracks,
  plates = [],
  objects = [],
  selectedTrackId,
  onSelectTrack,
  playRequest = null,
  recordedAt = null,
  onTime,
  subtitles = null,
  className,
}: VideoWithTracksProps) {
  const { t } = useTranslation()
  const label = useTrackLabel()
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const frameRef = useRef<HTMLDivElement | null>(null)
  const stopAtRef = useRef<number | null>(null)
  const [time, setTime] = useState(0)
  const onTimeRef = useRef(onTime)
  onTimeRef.current = onTime
  const [elementSize, setElementSize] = useState({ width: 0, height: 0 })
  const [pictureSize, setPictureSize] = useState({ width, height })

  // The element's size, so the boxes sit on the letterboxed picture whatever the pane's shape.
  useLayoutEffect(() => {
    const element = frameRef.current
    if (!element) return
    const measure = () =>
      setElementSize({ width: element.clientWidth, height: element.clientHeight })
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  // The clock (`followPlayback`), and the pause a second after a played pass.
  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    const onMetadata = () => {
      if (video.videoWidth && video.videoHeight) {
        setPictureSize({ width: video.videoWidth, height: video.videoHeight })
      }
    }
    video.addEventListener('loadedmetadata', onMetadata)
    onMetadata()
    const stopFollowing = followPlayback(video, seconds => {
      setTime(seconds)
      onTimeRef.current?.(seconds)
      const stopAt = stopAtRef.current
      if (stopAt !== null && seconds >= stopAt) {
        stopAtRef.current = null
        video.pause()
      }
    })
    return () => {
      stopFollowing()
      video.removeEventListener('loadedmetadata', onMetadata)
    }
  }, [])

  // "Play pass": seek, play, and stop a second past the pass — once per request.
  useEffect(() => {
    const video = videoRef.current
    if (!playRequest || !video) return
    stopAtRef.current = playRequest.to
    const start = () => {
      video.currentTime = playRequest.from
      void video.play().catch(() => undefined)
    }
    if (video.readyState >= 1) start()
    else video.addEventListener('loadedmetadata', start, { once: true })
    return () => video.removeEventListener('loadedmetadata', start)
  }, [playRequest])

  const seek = (seconds: number) => {
    const video = videoRef.current
    if (!video) return
    stopAtRef.current = null
    video.currentTime = seconds
    setTime(seconds)
    onTimeRef.current?.(seconds)
  }

  const rect = contentRect(
    elementSize.width,
    elementSize.height,
    pictureSize.width,
    pictureSize.height
  )
  const now = tracksAt(tracks, time)
  const platesNow = platesAt(plates, time)
  const objectsNow = objectsAt(objects, time)
  const bars = passBars(tracks, duration)
  const plateStrip = plateBars(plates, duration)

  return (
    <div className={cn('flex min-h-0 flex-col gap-2', className)} data-testid="video-with-tracks">
      <div
        ref={frameRef}
        className="relative min-h-0 flex-1 overflow-hidden rounded-md bg-background"
        data-testid="video-frame"
      >
        {/* biome-ignore lint/a11y/useMediaCaption: captions exist only for a recording whose sound was transcribed */}
        <video
          ref={videoRef}
          src={src ?? undefined}
          controls
          playsInline
          preload="metadata"
          className="size-full object-contain"
        >
          {/* What was said, under the player's own captions control, in full screen too (PRD-0115). */}
          {subtitles ? (
            <track
              kind="subtitles"
              src={subtitles.src}
              label={subtitles.label}
              srcLang={subtitles.language ?? undefined}
            />
          ) : null}
        </video>
        {/* The boxes sit between the picture and its controls; the strip is the seek surface. */}
        <div className="pointer-events-none absolute inset-0" data-time={time.toFixed(3)}>
          {now.map(entry => (
            <TrackBox
              key={entry.trackId}
              trackId={entry.trackId}
              rect={rect}
              box={entry.box}
              state={entry.face?.state ?? 'none'}
              label={label(entry.trackId, entry.face?.display_name)}
              selected={entry.trackId === selectedTrackId}
              onSelect={onSelectTrack}
              selectLabel={t(`${VIDEO}.selectBox`)}
            />
          ))}
          {objectsNow.map(object => (
            <div
              key={object.trackId}
              role="img"
              aria-label={t(`${VIDEO}.objectClass.${object.label}`, {
                defaultValue: t(`${VIDEO}.objectClass.other`),
              })}
              data-testid="video-object-box"
              data-object-label={object.label}
              className="absolute rounded-sm border border-dashed border-primary/70"
              style={boxStyle(object.box, rect)}
            >
              <span className="absolute -top-5 left-0 rounded-sm bg-background/90 px-1 text-xs text-foreground">
                {t(`${VIDEO}.objectClass.${object.label}`, {
                  defaultValue: t(`${VIDEO}.objectClass.other`),
                })}
              </span>
            </div>
          ))}
          {platesNow.map(plate => (
            <div
              key={plate.plateId}
              role="img"
              aria-label={t(`${VIDEO}.plateBox`, { text: plate.text })}
              data-testid="video-plate-box"
              data-plate-id={plate.plateId}
              data-plate-status={plate.status}
              className={cn('absolute rounded-sm border-2', PLATE_TONE[plate.status].box)}
              style={boxStyle(plate.box, rect)}
            >
              <span className="absolute -bottom-6 left-0 rounded-sm border border-border bg-background/90 px-1.5 py-0.5 font-mono text-xs text-foreground tabular-nums">
                {plate.text}
              </span>
            </div>
          ))}
        </div>
      </div>
      <VideoPassStrip
        bars={bars}
        plates={plateStrip}
        duration={duration}
        time={time}
        recordedAt={recordedAt}
        selectedTrackId={selectedTrackId}
        onSeek={seek}
        onPick={
          onSelectTrack
            ? (trackId, start) => {
                onSelectTrack(trackId)
                seek(start)
              }
            : (_trackId, start) => seek(start)
        }
      />
    </div>
  )
}

function TrackBox({
  trackId,
  rect,
  box,
  state,
  label,
  selected,
  onSelect,
  selectLabel,
}: {
  trackId: number
  rect: ContentRect
  box: readonly [number, number, number, number]
  state: PassBar['state']
  label: string
  selected: boolean
  onSelect?: (trackId: number | null) => void
  selectLabel: string
}) {
  const tone = TRACK_TONE[state]
  const style = boxStyle(box, rect)
  const shared = cn(
    'absolute rounded-sm border-2 transition-[border-color,box-shadow] duration-150',
    tone.box,
    selected ? 'ring-2 ring-primary ring-offset-1 ring-offset-background' : 'opacity-90'
  )
  const chip = (
    <span
      className={cn(
        'absolute -top-6 left-0 flex max-w-[16rem] items-center gap-1.5 truncate rounded-sm border border-border bg-background/90 px-1.5 py-0.5 text-xs text-foreground',
        selected && 'border-primary'
      )}
    >
      <span className={cn('size-1.5 shrink-0 rounded-full', tone.dot)} aria-hidden />
      <span className="truncate">{label}</span>
    </span>
  )
  if (!onSelect) {
    return (
      <div
        role="img"
        aria-label={label}
        data-testid="video-track-box"
        data-track-id={trackId}
        data-track-state={state}
        data-selected={selected ? 'true' : 'false'}
        className={shared}
        style={style}
      >
        {chip}
      </div>
    )
  }
  return (
    <button
      type="button"
      title={label}
      aria-label={`${label} — ${selectLabel}`}
      data-testid="video-track-box"
      data-track-id={trackId}
      data-track-state={state}
      data-selected={selected ? 'true' : 'false'}
      onClick={() => onSelect(trackId)}
      className={cn(
        shared,
        'pointer-events-auto cursor-pointer bg-transparent hover:border-primary'
      )}
      style={style}
    >
      {chip}
    </button>
  )
}

/** Greedy lanes: a pass takes the first lane free at its start; past the cap it shares the last. */
function lanesFor(bars: readonly PassBar[]): number[] {
  const laneEnds: number[] = []
  return bars.map(bar => {
    let lane = laneEnds.findIndex(end => end <= bar.start)
    if (lane === -1) lane = Math.min(laneEnds.length, MAX_LANES - 1)
    laneEnds[lane] = Math.max(laneEnds[lane] ?? 0, bar.end)
    return lane
  })
}

export interface VideoPassStripProps {
  bars: readonly PassBar[]
  /** The plates' bars, on their own lane under the people's. */
  plates?: readonly PlateBar[]
  duration: number
  time: number
  /** When the recording starts; with it the strip reads the time of day beside the elapsed time. */
  recordedAt?: string | null
  selectedTrackId: number | null
  onSeek: (seconds: number) => void
  /** A bar was picked: its pass, and the second it starts at. */
  onPick: (trackId: number, start: number) => void
}

/**
 * The recording's passes on its timeline: one bar per person in the tone of their face's state,
 * the playhead over them. Clicking a bar seeks to the pass; clicking the strip seeks there.
 */
export function VideoPassStrip({
  bars,
  plates = [],
  duration,
  time,
  recordedAt = null,
  selectedTrackId,
  onSeek,
  onPick,
}: VideoPassStripProps) {
  const { t, i18n } = useTranslation()
  const label = useTrackLabel()
  // To the second: the readout changes once a second, not once a frame.
  const clock = wallClockAt(recordedAt, Math.floor(time))
  const lanes = lanesFor(bars)
  const peopleLanes = Math.min(MAX_LANES, Math.max(1, ...lanes.map(lane => lane + 1)))
  // The plates share one lane under the people's; a recording without any adds none.
  const laneCount = peopleLanes + (plates.length > 0 ? 1 : 0)
  const playhead = duration > 0 ? Math.min(1, Math.max(0, time / duration)) : 0
  const stripRef = useRef<HTMLDivElement | null>(null)
  const seekAt = (clientX: number) => {
    const element = stripRef.current
    if (!element || duration <= 0) return
    const { left, width } = element.getBoundingClientRect()
    if (width <= 0) return
    onSeek(Math.min(duration, Math.max(0, ((clientX - left) / width) * duration)))
  }
  return (
    <div
      className="flex items-center gap-2 text-xs text-muted-foreground"
      data-testid="video-pass-strip"
    >
      <span className="w-12 shrink-0 tabular-nums">{formatVideoTime(time)}</span>
      {clock ? (
        <time
          dateTime={clock.toISOString()}
          title={t(`${VIDEO}.recording.wallClock`)}
          className="shrink-0 font-mono text-foreground tabular-nums"
          data-testid="video-wall-clock"
        >
          {formatClockTime(clock, i18n.language)}
        </time>
      ) : null}
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: the bars are the keyboard targets; the strip itself only seeks on a pointer */}
      <div
        ref={stripRef}
        role="group"
        aria-label={t(`${VIDEO}.passes`, { count: bars.length })}
        onClick={event => seekAt(event.clientX)}
        className="relative flex-1 cursor-pointer rounded-sm bg-muted"
        style={{ height: laneCount * LANE_HEIGHT + 6 }}
      >
        {bars.map((bar, index) => {
          const selected = bar.trackId === selectedTrackId
          const from = formatVideoTime(bar.start * duration)
          const to = formatVideoTime(bar.end * duration)
          return (
            <button
              key={bar.trackId}
              type="button"
              aria-label={t(`${VIDEO}.pass`, { name: label(bar.trackId, bar.label), from, to })}
              aria-pressed={selected}
              data-testid="video-pass-bar"
              data-track-id={bar.trackId}
              data-track-state={bar.state}
              onClick={event => {
                event.stopPropagation()
                onPick(bar.trackId, bar.start * duration)
              }}
              className={cn(
                'absolute h-2 rounded-full transition-[filter] hover:brightness-125',
                TRACK_TONE[bar.state].bar,
                selected && 'ring-2 ring-primary'
              )}
              style={{
                left: `${bar.start * 100}%`,
                width: `max(0.5%, ${(bar.end - bar.start) * 100}%)`,
                top: 3 + lanes[index] * LANE_HEIGHT,
              }}
            />
          )
        })}
        {plates.map(plate => (
          <button
            key={plate.plateId}
            type="button"
            aria-label={t(`${VIDEO}.plateBar`, {
              text: plate.text,
              from: formatVideoTime(plate.start * duration),
              to: formatVideoTime(plate.end * duration),
            })}
            data-testid="video-plate-bar"
            data-plate-id={plate.plateId}
            data-plate-status={plate.status}
            onClick={event => {
              event.stopPropagation()
              onSeek(plate.start * duration)
            }}
            className={cn(
              'absolute h-2 rounded-full transition-[filter] hover:brightness-125',
              PLATE_TONE[plate.status].bar
            )}
            style={{
              left: `${plate.start * 100}%`,
              width: `max(0.5%, ${(plate.end - plate.start) * 100}%)`,
              top: 3 + peopleLanes * LANE_HEIGHT,
            }}
          />
        ))}
        <div
          aria-hidden
          data-testid="video-playhead"
          className="pointer-events-none absolute inset-y-0 w-px bg-primary"
          style={{ left: `${playhead * 100}%` }}
        />
      </div>
      <span className="w-12 shrink-0 text-right tabular-nums">{formatVideoTime(duration)}</span>
    </div>
  )
}
